import React, { useCallback, useEffect, useRef, useState } from "react";
import "./ShopGame.css";
import {
    answer,
    clockRuns,
    coinPiece,
    CUSTOMERS,
    hintFor,
    inputModeOf,
    keypadAppend,
    keypadBackspace,
    lookOf,
    MAX_STARS,
    money,
    moneyEntryText,
    nextCustomer,
    parseEntry,
    patienceLeft,
    percentEntryText,
    resultOf,
    ShopAnswer,
    ShopLevel,
    ShopOrder,
    ShopPace,
    ShopResult,
    ShopState,
    startShift,
    tick,
    trayPiecesFor,
    TrayPiece,
    workedAnswerFor,
} from "./shopGame.ts";
import { useShopText } from "./shopText.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

/*
 * One Shop Rush shift (ADR 0131): a customer at the counter, an order card, and the
 * way to fill the order. The rules are shopGame.ts; this is the screen: what has been
 * tapped or typed, the clock, the message line, focus and the live region.
 *
 * Built in the first render from the engine's own start, so the first order is there
 * when the page paints and nothing moves when it arrives (ADR 0067). The engine draws
 * from the random source in the handlers here, never inside a state updater (ADR 0053
 * §8): the state lives in a ref with a snapshot published to React.
 *
 * Every place the page can change has its space held before it changes: the order card
 * keeps room for a pizza or a price board, the input area is as tall for the coin tray
 * as for the keypad, and the message line keeps room for the longest worked answer. Only
 * transforms and opacity move.
 *
 * Input. Change is built from a tray of bills and coins, only those worth less than the amount
 * paid (ADR 0131 §7); money, number and percent answers from a till keypad (money fills in from
 * the cents, like a till, so 7 then 5 shows $0.75; a percent shows a "%" after the digits); a better
 * buy from two offer buttons. The keypad also takes 0-9 and Backspace from a physical keyboard (Backspace takes back a piece on the tray),
 * 1 and 2 pick an offer, and Enter serves, or sends the next customer in once this one is
 * done. Enter is left alone on the Pause button and the offers, which take it themselves.
 *
 * The clock (Busy and Rush) runs only while a customer is waiting for an answer, on
 * animation-frame deltas the engine clamps. It pauses on the Pause button and by itself
 * when the tab is hidden; the order and the keypad are hidden while paused, so a pause is
 * not a way to read at leisure, and resuming is always a press.
 *
 * A live region reads each customer's order, each hint and each result. The visible
 * message line shows the hint or result without being a second live region.
 */

interface ShopGameRunProps {
    level: ShopLevel;
    pace: ShopPace;
    /** Called once, when the tenth customer has been seen. */
    onFinish(result: ShopResult): void;
}

/** Pieces the tray will hold at once; past this a tap is ignored, so the row never runs off. */
const MAX_PIECES = 60;

/** Pixels per millimetre of coin, so a nickel is bigger than a dime and a quarter biggest; nothing is under the 44px target. */
const COIN_SCALE = 2.6;
const MIN_TARGET = 44;
/** The coins in a count-the-coins order are only looked at, never tapped, so they are drawn smaller than the tray's and keep the order card one height. */
const PURSE_SCALE = 2;

const SKINS = ["#f6d2b0", "#c68642", "#8d5524", "#ffdbac", "#e0ac69", "#a1665e"];
const SHIRTS = ["#2563eb", "#db2777", "#059669", "#d97706", "#7c3aed", "#0891b2"];
const HAIRS = ["#2b1b0e", "#6b3e1e", "#111827", "#b45309", "#4b2e83", "#7c2d12"];

/** A simple original figure behind the counter. `mood` changes only the mouth. */
function Customer({ look, mood }: { look: number; mood: "waiting" | "happy" | "gone" }) {
    const skin = SKINS[look % SKINS.length];
    const shirt = SHIRTS[(look + 2) % SHIRTS.length];
    const hair = HAIRS[(look + 4) % HAIRS.length];
    const style = look % 3;
    return (
        <svg className="shopFigure" viewBox="0 0 80 100" aria-hidden="true" focusable="false">
            <rect x="14" y="52" width="52" height="48" rx="14" fill={shirt} />
            <rect x="32" y="44" width="16" height="14" rx="5" fill={skin} />
            <circle cx="40" cy="30" r="20" fill={skin} />
            {style === 0 && <path d="M18 28 A22 22 0 0 1 62 28 Q50 18 40 20 Q28 18 18 28 Z" fill={hair} />}
            {style === 1 && (
                <path d="M17 34 A23 24 0 0 1 63 34 L63 44 Q60 30 52 22 Q40 28 28 22 Q20 30 17 44 Z" fill={hair} />
            )}
            {style === 2 && (
                <path d="M19 26 L24 10 L32 20 L40 6 L48 20 L56 10 L61 26 Q40 16 19 26 Z" fill={hair} />
            )}
            <circle cx="33" cy="32" r="2.6" fill="#1d1f20" />
            <circle cx="47" cy="32" r="2.6" fill="#1d1f20" />
            {mood === "happy" && <path d="M31 40 Q40 49 49 40" stroke="#1d1f20" strokeWidth="2.6" fill="none" strokeLinecap="round" />}
            {mood === "waiting" && <path d="M33 42 L47 42" stroke="#1d1f20" strokeWidth="2.6" fill="none" strokeLinecap="round" />}
            {mood === "gone" && <path d="M32 45 Q40 38 48 45" stroke="#1d1f20" strokeWidth="2.6" fill="none" strokeLinecap="round" />}
        </svg>
    );
}

/** A pizza cut into `slices` equal slices: nothing shaded, so the picture does not give the answer away. */
function Pizza({ slices }: { slices: number }) {
    const spokes = Array.from({ length: slices }, (_, i) => {
        const angle = (2 * Math.PI * i) / slices - Math.PI / 2;
        return { x: 60 + 52 * Math.cos(angle), y: 60 + 52 * Math.sin(angle), key: i };
    });
    const dots = Array.from({ length: slices }, (_, i) => {
        const angle = (2 * Math.PI * (i + 0.5)) / slices - Math.PI / 2;
        return { x: 60 + 30 * Math.cos(angle), y: 60 + 30 * Math.sin(angle), key: i };
    });
    return (
        <svg className="shopPizza" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
            <circle cx="60" cy="60" r="56" fill="#d9954a" />
            <circle cx="60" cy="60" r="50" fill="#f6cf75" />
            {dots.map((dot) => (
                <circle key={dot.key} cx={dot.x} cy={dot.y} r="5" fill="#c0392b" />
            ))}
            {spokes.map((spoke) => (
                <line key={spoke.key} x1="60" y1="60" x2={spoke.x} y2={spoke.y} stroke="#9a5b1c" strokeWidth="2" />
            ))}
        </svg>
    );
}

function coinSize(piece: TrayPiece): number {
    return Math.max(MIN_TARGET, Math.round((piece.diameter ?? 0) * COIN_SCALE));
}

/** "$10", "$5", "$1" for bills, "25¢" for coins: what is printed on the piece. */
function pieceLabel(piece: TrayPiece): string {
    return piece.kind === "bill" ? `$${piece.cents / 100}` : `${piece.cents}¢`;
}

/** The result lines that need no figures. `labelKey` so StringKeys.test.js sees the keys. */
const RESULT_KEYS = {
    servedFirst: { labelKey: "flashCards.shopServedFirst" },
    servedSecond: { labelKey: "flashCards.shopServedSecond" },
    leftWrong: { labelKey: "flashCards.shopLeftWrongTemplate" },
    leftPatience: { labelKey: "flashCards.shopLeftPatienceTemplate" },
} satisfies Record<string, { labelKey: StringKey }>;

const KEYPAD_KEYS = ["7", "8", "9", "4", "5", "6", "1", "2", "3"];

export function ShopGameRun({ level, pace, onFinish }: ShopGameRunProps) {
    const t = useTranslation();
    const text = useShopText();

    const stateRef = useRef<ShopState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startShift(level, pace, getRandomInt);
    }
    const [game, setGame] = useState<ShopState>(stateRef.current);
    const [entry, setEntry] = useState("");
    const [pieces, setPieces] = useState<number[]>([]);
    const [choice, setChoice] = useState<"a" | "b" | null>(null);
    const [message, setMessage] = useState("");
    const [announce, setAnnounce] = useState("");
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(false);
    pausedRef.current = paused;
    const reported = useRef(false);
    const nextRef = useRef<HTMLButtonElement>(null);

    const order = game.order;
    const mode = inputModeOf(order);
    const waiting = game.phase === "order" || game.phase === "hint";
    const over = game.phase === "over";
    const timed = game.patienceMs !== null;
    const left = patienceLeft(game);
    const percent = left === null ? 0 : Math.round(left * 100);

    /** The line read out and shown for a state a move or the clock has just reached. */
    function resultMessage(next: ShopState): string {
        const event = next.lastEvent;
        if (event.kind === "served") {
            return t(event.stars === 2 ? RESULT_KEYS.servedFirst.labelKey : RESULT_KEYS.servedSecond.labelKey);
        }
        if (event.kind === "miss") {
            return format(t("flashCards.shopMissTemplate"), { hint: text.messageText(hintFor(next.order)) });
        }
        if (event.kind === "left") {
            const worked = text.messageText(workedAnswerFor(next.order));
            return format(
                t(event.reason === "patience" ? RESULT_KEYS.leftPatience.labelKey : RESULT_KEYS.leftWrong.labelKey),
                { worked }
            );
        }
        return "";
    }

    function arrival(state: ShopState): string {
        return format(t("flashCards.shopArriveTemplate"), { n: state.customer, order: text.announceText(state.order) });
    }

    // The first customer is read out once the page is up: a live region only speaks a change.
    useEffect(() => {
        setAnnounce(arrival(stateRef.current!));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function resetInput() {
        setEntry("");
        setPieces([]);
        setChoice(null);
    }

    /** Publishes a state the engine has just made, with the line that says what happened. */
    function commit(next: ShopState, say: boolean) {
        stateRef.current = next;
        setGame(next);
        if (say) {
            const line = resultMessage(next);
            setMessage(line);
            setAnnounce(line);
        }
    }

    // The clock: animation-frame deltas into the engine, published when the bar moves a whole percent or the phase changes.
    const clock = clockRuns(game) && !paused;
    useEffect(() => {
        if (!clock) {
            return undefined;
        }
        let frame = 0;
        let last = performance.now();
        const loop = (now: number) => {
            const before = stateRef.current!;
            const next = tick(before, now - last);
            last = now;
            if (next !== before) {
                const full = (s: ShopState) => Math.round((patienceLeft(s) ?? 0) * 100);
                stateRef.current = next;
                if (next.phase !== before.phase) {
                    commit(next, true);
                } else if (full(next) !== full(before)) {
                    setGame(next);
                }
            }
            if (stateRef.current!.phase === "order" || stateRef.current!.phase === "hint") {
                frame = requestAnimationFrame(loop);
            }
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clock, game.customer]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden && stateRef.current!.patienceMs !== null) {
                setPaused(true);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            onFinish(resultOf(stateRef.current!));
        }
    }, [over, onFinish]);

    // Once a customer is done, the button that sends in the next is where the focus is, so Enter and Space both go on.
    useEffect(() => {
        if (game.phase === "served" || game.phase === "left") {
            nextRef.current?.focus({ preventScroll: true });
        }
    }, [game.phase, game.customer]);

    const given = pieces.reduce((sum, cents) => sum + cents, 0);

    /** What the player has put together so far, or null if there is nothing to serve yet. */
    function currentValue(): ShopAnswer | null {
        if (mode === "tray") {
            return pieces.length > 0 ? given : null;
        }
        if (mode === "choice") {
            return choice;
        }
        return parseEntry(entry, mode);
    }

    function serve() {
        const state = stateRef.current!;
        const value = currentValue();
        if (pausedRef.current || value === null || (state.phase !== "order" && state.phase !== "hint")) {
            return;
        }
        resetInput();
        commit(answer(state, value), true);
    }

    function next() {
        const state = stateRef.current!;
        if (pausedRef.current || (state.phase !== "served" && state.phase !== "left")) {
            return;
        }
        const upcoming = nextCustomer(state, getRandomInt);
        resetInput();
        if (upcoming.phase === "over") {
            stateRef.current = upcoming;
            setGame(upcoming);
            return;
        }
        commit(upcoming, false);
        setMessage("");
        setAnnounce(arrival(upcoming));
    }

    function addPiece(cents: number) {
        if (waiting && !paused && pieces.length < MAX_PIECES) {
            setPieces((all) => [...all, cents]);
        }
    }

    function press(key: string) {
        if (waiting && !paused && (mode === "money" || mode === "number" || mode === "percent")) {
            setEntry((text0) => keypadAppend(text0, key, mode));
        }
    }

    function backspace() {
        if (!waiting || paused) {
            return;
        }
        if (mode === "tray") {
            setPieces((all) => all.slice(0, -1));
        } else if (mode === "money" || mode === "number" || mode === "percent") {
            setEntry(keypadBackspace);
        }
    }

    // The keyboard: listened for on the document, through a ref so it always sees this render's handlers.
    const keyHandler = useRef<(event: KeyboardEvent) => void>(() => {});
    keyHandler.current = (event: KeyboardEvent) => {
        if (event.ctrlKey || event.metaKey || event.altKey || pausedRef.current) {
            return;
        }
        const target = event.target instanceof Element ? event.target : null;
        if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) {
            return;
        }
        const state = stateRef.current!;
        if (event.key === "Enter") {
            if (target?.closest("[data-own-enter]")) {
                return;
            }
            event.preventDefault();
            if (state.phase === "order" || state.phase === "hint") {
                serve();
            } else {
                next();
            }
            return;
        }
        if (state.phase !== "order" && state.phase !== "hint") {
            return;
        }
        if (event.key === "Backspace") {
            event.preventDefault();
            backspace();
        } else if (mode === "choice" && (event.key === "1" || event.key === "2")) {
            setChoice(event.key === "1" ? "a" : "b");
        } else if (event.key.length === 1) {
            press(event.key);
        }
    };
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => keyHandler.current(event);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, []);

    const togglePause = useCallback(() => setPaused((p) => !p), []);

    const mood = game.phase === "served" ? "happy" : game.phase === "left" ? "gone" : "waiting";
    const lastCustomer = game.customer >= CUSTOMERS;
    const canServe = waiting && !paused && currentValue() !== null;

    return (
        <div className="shopRun">
            <p className="shopStatus">
                {format(t("flashCards.shopStatusTemplate"), {
                    n: game.customer,
                    total: CUSTOMERS,
                    stars: game.stars,
                    max: MAX_STARS,
                })}
            </p>

            <div className="shopClockRow">
                {timed && (
                    <>
                        <div
                            className={`shopPatience${percent <= 25 ? " shopPatience--low" : ""}`}
                            role="progressbar"
                            aria-label={t("flashCards.shopPatienceLabel")}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={percent}
                        >
                            <div className="shopPatienceFill" style={{ transform: `scaleX(${left ?? 0})` }} />
                        </div>
                        <button
                            type="button"
                            className="btn btn-secondary shopPause"
                            data-own-enter=""
                            aria-pressed={paused}
                            onClick={togglePause}
                        >
                            {paused ? t("flashCards.shopResume") : t("flashCards.shopPause")}
                        </button>
                    </>
                )}
            </div>

            <div className={`shopPlay${paused ? " shopPlay--paused" : ""}`}>
                {paused && <p className="shopPausedNote">{t("flashCards.shopPausedNote")}</p>}
                <div className="shopStage" role="group" aria-label={t("flashCards.shopCounterLabel")}>
                    <div key={game.customer} className={`shopCustomer shopCustomer--${mood}`}>
                        <Customer look={lookOf(game)} mood={mood} />
                    </div>
                    <div className="shopOrderCard">
                        <p className="shopOrderText">{text.orderText(order)}</p>
                        {order.kind === "fraction" && <Pizza slices={order.slices} />}
                        {order.kind === "total" && <PriceBoard order={order} />}
                        {order.kind === "coins" && <CoinsPurse coins={order.coins} />}
                    </div>
                </div>

                <p className="shopMessage">{message}</p>

                <div className="shopInput">
                    {mode === "tray" && (
                        <Tray
                            pieces={pieces}
                            given={given}
                            offered={order.kind === "change" ? trayPiecesFor(order.paidCents) : []}
                            disabled={!waiting || paused}
                            onAdd={addPiece}
                        />
                    )}
                    {(mode === "money" || mode === "number" || mode === "percent") && (
                        <Keypad entry={entry} mode={mode} disabled={!waiting || paused} onPress={press} onBackspace={backspace} />
                    )}
                    {mode === "choice" && order.kind === "betterBuy" && (
                        <div className="shopOffers" role="group" aria-label={t("flashCards.shopOffersLabel")}>
                            {(["a", "b"] as const).map((side) => (
                                <button
                                    key={side}
                                    type="button"
                                    className="shopOffer"
                                    data-own-enter=""
                                    aria-pressed={choice === side}
                                    disabled={!waiting || paused}
                                    onClick={() => setChoice(side)}
                                >
                                    {text.offerText(order[side])}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="shopActions">
                {waiting ? (
                    <>
                        {mode === "tray" && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-secondary shopSmall"
                                    disabled={!waiting || paused || pieces.length === 0}
                                    onClick={backspace}
                                >
                                    {t("flashCards.shopUndo")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary shopSmall"
                                    disabled={!waiting || paused || pieces.length === 0}
                                    onClick={() => setPieces([])}
                                >
                                    {t("flashCards.shopClear")}
                                </button>
                            </>
                        )}
                        <button type="button" className="btn btn-primary shopServe" disabled={!canServe} onClick={serve}>
                            {t("flashCards.shopServe")}
                        </button>
                    </>
                ) : (
                    <button type="button" className="btn btn-primary shopServe" ref={nextRef} onClick={next}>
                        {lastCustomer ? t("flashCards.shopFinish") : t("flashCards.shopNextCustomer")}
                    </button>
                )}
            </div>

            <p className="srOnly" role="status" aria-live="polite">
                {announce}
            </p>
        </div>
    );
}

function PriceBoard({ order }: { order: Extract<ShopOrder, { kind: "total" }> }) {
    const t = useTranslation();
    const text = useShopText();
    return (
        <ul className="shopBoard" aria-label={t("flashCards.shopBoardLabel")}>
            {order.items.map((line) => (
                <li key={line.id}>
                    <span>{text.itemName(line.id, 1)}</span>
                    <span>{money(line.cents)}</span>
                </li>
            ))}
        </ul>
    );
}

/** The coins a count-the-coins order is paid with, drawn the size of the tray coins (grade 2). */
function CoinsPurse({ coins }: { coins: number[] }) {
    const t = useTranslation();
    return (
        <ul className="shopPurse" aria-label={t("flashCards.shopCoinsLabel")}>
            {coins.map((cents, i) => {
                const size = Math.round((coinPiece(cents).diameter ?? 0) * PURSE_SCALE);
                return (
                    <li
                        key={i}
                        className={`shopPiece shopPiece--coin shopPiece--${cents} shopPurseCoin`}
                        style={{ width: size, height: size }}
                    >
                        {cents}¢
                    </li>
                );
            })}
        </ul>
    );
}

interface TrayProps {
    pieces: number[];
    given: number;
    /** Only the pieces worth less than the amount paid. */
    offered: TrayPiece[];
    disabled: boolean;
    onAdd(cents: number): void;
}

function Tray({ pieces, given, offered, disabled, onAdd }: TrayProps) {
    const t = useTranslation();
    // A row with nothing in it (no bills in change from $1.00) is not drawn at all.
    const row = (kind: "bill" | "coin") => {
        const rowPieces = offered.filter((piece) => piece.kind === kind);
        if (rowPieces.length === 0) {
            return null;
        }
        return (
            <div className={`shopTrayRow shopTrayRow--${kind}`}>
                {rowPieces.map((piece) => {
                    const size = coinSize(piece);
                    return (
                        <button
                            key={piece.cents}
                            type="button"
                            className={`shopPiece shopPiece--${kind} shopPiece--${piece.cents}`}
                            style={kind === "coin" ? { width: size, height: size } : undefined}
                            aria-label={format(t("flashCards.shopAddTemplate"), { amount: money(piece.cents) })}
                            disabled={disabled}
                            onClick={() => onAdd(piece.cents)}
                        >
                            {pieceLabel(piece)}
                        </button>
                    );
                })}
            </div>
        );
    };
    return (
        <div className="shopTray" role="group" aria-label={t("flashCards.shopTrayLabel")}>
            <p className="shopGiven">{format(t("flashCards.shopGivenTemplate"), { amount: money(given) })}</p>
            <ul className="shopGivenPieces" aria-hidden="true">
                {pieces.map((cents, i) => (
                    <li key={i}>{cents >= 100 ? `$${cents / 100}` : `${cents}¢`}</li>
                ))}
            </ul>
            {row("bill")}
            {row("coin")}
        </div>
    );
}

interface KeypadProps {
    entry: string;
    mode: "money" | "number" | "percent";
    disabled: boolean;
    onPress(key: string): void;
    onBackspace(): void;
}

function Keypad({ entry, mode, disabled, onPress, onBackspace }: KeypadProps) {
    const t = useTranslation();
    return (
        <div className="shopKeypad" role="group" aria-label={t("flashCards.shopKeypadLabel")}>
            <p className="shopDisplay">
                <span className="srOnly">{t("flashCards.shopEntryLabel")}</span>
                <span className="shopEntry">
                    {mode === "money" ? moneyEntryText(entry) : mode === "percent" ? percentEntryText(entry) : entry === "" ? "0" : entry}
                </span>
            </p>
            <div className="shopKeys">
                {KEYPAD_KEYS.map((key) => (
                    <button key={key} type="button" className="shopKey" disabled={disabled} onClick={() => onPress(key)}>
                        {key}
                    </button>
                ))}
                <span className="shopKeyGap" aria-hidden="true" />
                <button type="button" className="shopKey" disabled={disabled} onClick={() => onPress("0")}>
                    0
                </button>
                <button
                    type="button"
                    className="shopKey"
                    aria-label={t("flashCards.shopBackspace")}
                    disabled={disabled}
                    onClick={onBackspace}
                >
                    ⌫
                </button>
            </div>
        </div>
    );
}

export default ShopGameRun;
